import { authz } from '@dreams-built/shared/src/auth/permissions';
import {
  type QuoteRequestStatus,
  type QuoteUploadRequest
} from '@dreams-built/shared/src/quotes/types';
import { useAuthOptionalUser } from 'utils/contexts/AuthProvider';
import { useFeature } from './features';
import { api } from './trpc';

/** ----------------------------- Public ----------------------------- */

export const useRequestUploadUrlMutation = () =>
  api.quoting.public.requestUploadUrl.useMutation();

export const useSubmitQuoteRequestMutation = () =>
  api.quoting.public.submitRequest.useMutation();

export const usePublicQuote = (token: string) =>
  api.quoting.public.getQuoteByToken.useQuery({ token }, { retry: 1 });

export const useRespondToQuoteMutation = () => {
  const utils = api.useUtils();
  return api.quoting.public.respondToQuote.useMutation({
    onSuccess: () => utils.quoting.public.getQuoteByToken.invalidate()
  });
};

/** Upload one file via presigned PUT; resolves to the stored key. */
export const uploadQuoteFile = async (
  requestUrl: (input: QuoteUploadRequest) => Promise<{ key: string; uploadUrl: string }>,
  file: File
) => {
  const { key, uploadUrl } = await requestUrl({
    name: file.name,
    contentType: file.type as QuoteUploadRequest['contentType'],
    size: file.size
  });

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type }
  });
  if (!response.ok) throw new Error(`Upload failed for ${file.name}`);

  return { key, name: file.name, size: file.size, contentType: file.type };
};

/** ---------------------------- Internal ---------------------------- */

export const useQuoteRequests = (input: {
  page: number;
  perPage?: number;
  status?: QuoteRequestStatus;
  query?: string;
}) =>
  api.quoting.requests.list.useQuery({
    page: input.page,
    perPage: input.perPage ?? 25,
    status: input.status,
    query: input.query
  });

export const useQuoteRequest = (id: string) => api.quoting.requests.get.useQuery({ id });

/**
 * Requests nobody has triaged yet, for the Quotes nav badge and the
 * notification bell. Both live in chrome that renders for every signed-in
 * user, so this gates itself on the feature and permission rather than making
 * each caller remember — otherwise it fires for people the server refuses.
 */
export const useNewQuoteRequests = () => {
  const { available } = useFeature('quoting');
  const { user } = useAuthOptionalUser();
  const canRead = user?.permissions?.includes(authz.quotes_read) ?? false;

  return api.quoting.requests.newSummary.useQuery(
    { take: 5 },
    { enabled: available && canRead, refetchOnWindowFocus: true, retry: false }
  );
};

export const useCreateQuoteRequestMutation = () => {
  const utils = api.useUtils();
  return api.quoting.requests.create.useMutation({
    onSuccess: () => utils.quoting.requests.invalidate()
  });
};

export const useUpdateQuoteRequestDetailsMutation = () => {
  const utils = api.useUtils();
  return api.quoting.requests.updateDetails.useMutation({
    onSuccess: () => utils.quoting.requests.invalidate()
  });
};

export const useUpdateRequestStatusMutation = () => {
  const utils = api.useUtils();
  return api.quoting.requests.updateStatus.useMutation({
    onSuccess: () => utils.quoting.requests.invalidate()
  });
};

export const useSaveRequestNotesMutation = () => {
  const utils = api.useUtils();
  return api.quoting.requests.saveNotes.useMutation({
    onSuccess: () => utils.quoting.requests.invalidate()
  });
};

export const useRateItems = () => api.quoting.rates.list.useQuery();

export const useCreateRateItemMutation = () => {
  const utils = api.useUtils();
  return api.quoting.rates.create.useMutation({
    onSuccess: () => utils.quoting.rates.list.invalidate()
  });
};

export const useUpdateRateItemMutation = () => {
  const utils = api.useUtils();
  return api.quoting.rates.update.useMutation({
    onSuccess: () => utils.quoting.rates.list.invalidate()
  });
};

export const useRemoveRateItemMutation = () => {
  const utils = api.useUtils();
  return api.quoting.rates.remove.useMutation({
    onSuccess: () => utils.quoting.rates.list.invalidate()
  });
};

export const useSeedStarterRatesMutation = () => {
  const utils = api.useUtils();
  return api.quoting.rates.seedStarter.useMutation({
    onSuccess: () => utils.quoting.rates.list.invalidate()
  });
};

export const useQuoteSettings = () => api.quoting.rates.getSettings.useQuery();

export const useUpdateQuoteSettingsMutation = () => {
  const utils = api.useUtils();
  return api.quoting.rates.updateSettings.useMutation({
    onSuccess: () => utils.quoting.rates.getSettings.invalidate()
  });
};

export const useQuotes = (input: { page: number; perPage?: number }) =>
  api.quoting.quotes.list.useQuery({ page: input.page, perPage: input.perPage ?? 25 });

export const useQuote = (id: string) => api.quoting.quotes.get.useQuery({ id });

export const useCreateQuoteFromRequestMutation = () => {
  const utils = api.useUtils();
  return api.quoting.quotes.createFromRequest.useMutation({
    onSuccess: () => utils.quoting.invalidate()
  });
};

export const useUpdateQuoteMutation = () => {
  const utils = api.useUtils();
  return api.quoting.quotes.update.useMutation({
    onSuccess: () => utils.quoting.quotes.invalidate()
  });
};

export const useConvertQuoteToProjectMutation = () => {
  const utils = api.useUtils();
  return api.quoting.quotes.convertToProject.useMutation({
    onSuccess: () => utils.quoting.invalidate()
  });
};
